import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface LastLoginActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  control: Control<PolicyFormValues>;
}

export const LastLoginActionSchema = z
  .object({
    type: z.literal('Last_Login_Action'),
    daysNotLoggedIn: z.number().nullable(),
    lastLoginActionType: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.daysNotLoggedIn === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['daysNotLoggedIn'],
        message: 'A number is required.',
      });
    } else if (!data.daysNotLoggedIn || data.daysNotLoggedIn < 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['daysNotLoggedIn'],
        message: 'Must be greater than 0.',
      });
    }
  });

export type LastLoginActionType = z.infer<typeof LastLoginActionSchema>;

// Type guard to check if an action is LastLoginActionType
function isLastLoginAction(
  action: PolicyAction
): action is LastLoginActionType {
  return action.type === 'Last_Login_Action';
}

export default function LastLoginAction({
  setValue,
  watch,
  errors,
  control,
}: LastLoginActionProps) {
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Last_Login_Action');
  const actionAtIndex = actionData[idx];
  if (!isLastLoginAction(actionAtIndex)) {
    return null;
  }

  const currentAction: LastLoginActionType = actionAtIndex;
  const error: FieldErrors<LastLoginActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={1}>
        <Controller
          name={`actionData.${idx}.daysNotLoggedIn`}
          control={control}
          render={() => (
            <TextField
              inputProps={{
                id: 'daysNotLoggedIn',
                min: 1,
              }}
              InputLabelProps={{
                htmlFor: 'daysNotLoggedIn',
              }}
              type="number"
              label="Days since last log in:"
              value={currentAction.daysNotLoggedIn}
              onChange={(event) => {
                const value =
                  event.target.value === ''
                    ? null
                    : parseInt(event.target.value);
                setValue(`actionData.${idx}.daysNotLoggedIn`, value, {
                  shouldValidate: true,
                });
              }}
              error={!!error?.daysNotLoggedIn}
              helperText={error?.daysNotLoggedIn?.message || ' '}
              sx={{ width: 250 }}
              autoComplete="off"
            />
          )}
        />
        <Controller
          name={`actionData.${idx}.lastLoginActionType`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              inputProps={{
                id: 'lastLoginActionType',
              }}
              InputLabelProps={{
                htmlFor: 'lastLoginActionType',
              }}
              name="lastLoginActionType"
              select
              label="Action Type"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value={'suspend'}>Suspend</MenuItem>
              <MenuItem value={'delete'}>Delete</MenuItem>
            </TextField>
          )}
        />
      </Stack>
    </Stack>
  );
}
